/**
 * Hardware (US only) is priced by staff-api, not by this engine: the same Kiosk freight bands
 * and Stripe-priced configurations Create Hardware Only Invoice bills from
 * (`staffQuoteHardwarePrice`). This file folds that answer into the engine's quote so the
 * totals, warnings and printed document stay one object.
 *
 * A device row's `sku` holds the option key the server returns — `sku`, or `sku|price_…` for a
 * Stripe-priced configuration — so a row finds its price by equality.
 */
import type { PricedLine, PricedQuote, QuoteRequestLine } from './types';

export interface HardwarePriceLine {
  kind: string;
  key: string;
  label: string;
  quantity: number;
  unitAmountMinor: number;
  amountMinor: number;
}

/**
 * The rows complete enough to send for pricing, one entry per option. staff-api refuses the
 * same option twice, and freight is per shipment, so two rows of one product (a deep link can
 * add a second) are priced as their combined quantity.
 */
export function hardwareRequest(rows: QuoteRequestLine[]) {
  const qty = new Map<string, number>();
  for (const r of rows) {
    if (r.sku && typeof r.quantity === 'number' && r.quantity > 0) {
      qty.set(r.sku, (qty.get(r.sku) ?? 0) + r.quantity);
    }
  }
  return [...qty].map(([key, quantity]) => {
    const [sku, stripePriceId = ''] = key.split('|');
    return { sku, stripePriceId, quantity };
  });
}

export function withHardware(
  base: PricedQuote,
  rows: QuoteRequestLine[],
  priced: HardwarePriceLine[] | null,
  error: string | null,
): PricedQuote {
  if (!rows.length) return base;
  const hitl = [...base.hitl];
  const lines: PricedLine[] = [...base.lines];

  for (const row of rows) {
    const hit = priced?.find((p) => p.kind === 'hardware' && p.key === row.sku);
    if (!row.sku) hitl.push('hardware: choose a product');
    else if (!hit && !error && priced) hitl.push(`hardware '${row.sku}': no price`);
    const qty = row.quantity ?? 0;
    lines.push({
      rowId: row.id,
      kind: 'device',
      label: hit?.label ?? row.sku ?? 'hardware',
      quantity: qty,
      sku: row.sku,
      unitCents: hit ? hit.unitAmountMinor : null,
      amountCents: hit && qty > 0 ? hit.unitAmountMinor * qty : null,
    });
  }
  if (error) hitl.push(`hardware: ${error}`);

  for (const s of priced?.filter((p) => p.kind === 'shipping') ?? []) {
    lines.push({
      kind: 'shipping',
      label: s.label,
      quantity: s.quantity,
      region: 'internal',
      unitCents: s.unitAmountMinor,
      amountCents: s.amountMinor,
      autoAdded: true,
    });
  }

  const added = lines
    .slice(base.lines.length)
    .reduce((sum, l) => sum + (l.amountCents ?? 0), 0);
  return {
    ...base,
    hitl,
    lines,
    ready: base.ready && hitl.length === base.hitl.length && lines.every((l) => l.amountCents !== null),
    subtotalCents: base.subtotalCents + added,
    totalCents: base.totalCents + added,
  };
}
